/**
 * InputRenderer — main orchestrator inside the iframe.
 *
 * Renders secure input fields (combined or split), wires validation,
 * styling, accessibility, and handles all PostMessage communication.
 *
 * Requirements: 11.1, 11.2, 11.3, 13.3, 13.4, 13.5, 13.6, 14.5
 */
import { ValidationEngine, type FieldValidationResult } from "./validation/ValidationEngine";
import { StyleEngine, type Appearance, type ComputedStyles } from "./style/StyleEngine";
import { AccessibilityManager } from "./a11y/AccessibilityManager";
import { JWEEncryptor, type CardDataPayload } from "./crypto/JWEEncryptor";
import type { CardBrand } from "./validation/BrandDetector";
import { createTranslationManager, type TranslationManager, type TranslationKey, type TranslationOverride } from "./i18n/TranslationManager";
import { RTLDirector } from "./i18n/RTLDirector";

/**
 * Maps ValidationEngine error codes to translation keys for localised error messages.
 * The error.code remains the stable English identifier for programmatic matching;
 * the error.message is replaced with the translated string.
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7
 */
export const ERROR_CODE_TO_KEY: Record<string, TranslationKey> = {
  "invalid_card_number": "errors.invalidCardNumber",
  "incomplete_card_number": "errors.incompleteCardNumber",
  "invalid_expiry": "errors.invalidExpiryDate",
  "incomplete_expiry": "errors.incompleteExpiryDate",
  "expired_card": "errors.expiredCard",
  "invalid_cvc": "errors.invalidCvc",
  "incomplete_cvc": "errors.incompleteCvc",
};

export interface ChangeEventPayload {
  elementType: string;
  complete: boolean;
  empty: boolean;
  valid: boolean;
  brand: CardBrand | null;
  error: { code: string; message: string } | null;
}

export interface SimpleEventPayload {
  elementType: string;
}

type OutgoingAction = "ready" | "change" | "focus" | "blur" | "escape" | "tokenise_result";

interface OutgoingMessage {
  action: OutgoingAction;
  payload: unknown;
}

interface IncomingMessage {
  action: string;
  payload?: unknown;
}

export interface InputRendererConfig {
  merchantOrigin: string;
  elementType: string;
}

/**
 * Build a ChangeEventPayload from a validation result and element type.
 * Ensures all six required fields are always present.
 * Requirements: 7.5, 27.1, 27.2, 27.3, 27.4, 27.5
 */
export function buildChangePayload(
  elementType: string,
  result: FieldValidationResult
): ChangeEventPayload {
  return {
    elementType,
    complete: result.complete,
    empty: result.empty,
    valid: result.valid,
    brand: result.brand,
    error: result.error,
  };
}

/**
 * Build a ChangeEventPayload for a combined card element from all field results.
 */
export function buildCombinedChangePayload(
  elementType: string,
  cardNumber: FieldValidationResult,
  expiry: FieldValidationResult,
  cvc: FieldValidationResult
): ChangeEventPayload {
  const allComplete = cardNumber.complete && expiry.complete && cvc.complete;
  const anyEmpty = cardNumber.empty && expiry.empty && cvc.empty;
  // Find the first error from any field
  const firstError = cardNumber.error ?? expiry.error ?? cvc.error;
  // Valid if no errors exist
  const allValid = cardNumber.valid && expiry.valid && cvc.valid;

  return {
    elementType,
    complete: allComplete,
    empty: anyEmpty,
    valid: allValid,
    brand: cardNumber.brand,
    error: firstError,
  };
}

export class InputRenderer {
  private config: InputRendererConfig;
  private container: HTMLElement | null = null;
  private inputs: Map<string, HTMLInputElement> = new Map();
  private cardData: CardDataPayload = { cardNumber: "", expiry: "", cvc: "" };
  private computedStyles: ComputedStyles | null = null;
  private cleanupFns: Array<() => void> = [];
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private translationManager: TranslationManager;

  constructor(config: InputRendererConfig) {
    this.config = config;
    this.translationManager = createTranslationManager();
  }

  /**
   * Initialise the renderer: create inputs, apply styles, set up a11y, start listening.
   */
  init(container: HTMLElement, appearance?: Appearance, locale?: string, translations?: TranslationOverride): void {
    this.container = container;
    this.computedStyles = StyleEngine.compute(appearance);
    StyleEngine.apply(container, this.computedStyles);

    // Initialise TranslationManager with locale and optional overrides
    this.translationManager.init(locale ?? "en", translations);
    RTLDirector.apply(container, this.translationManager.isRTL());

    if (this.config.elementType === "card") {
      this.createCombinedInputs(container);
    } else {
      this.createSingleInput(container, this.config.elementType);
    }

    this.setupPostMessageListener();
    this.sendMessage("ready", { elementType: this.config.elementType });
  }

  /**
   * Handle incoming PostMessage actions from the merchant page.
   * Validates origin before processing.
   * Requirements: 13.3, 13.6
   */
  private setupPostMessageListener(): void {
    this.messageHandler = (event: MessageEvent) => {
      // Origin validation — discard messages from unrecognised origins
      if (event.origin !== this.config.merchantOrigin) {
        return;
      }

      const data = event.data as IncomingMessage;
      if (!data || typeof data.action !== "string") return;

      switch (data.action) {
        case "init":
          this.handleInit(data.payload as { appearance?: Appearance; locale?: string; translations?: TranslationOverride });
          break;
        case "focus":
          this.handleFocus();
          break;
        case "blur":
          this.handleBlur();
          break;
        case "clear":
          this.handleClear();
          break;
        case "tokenise":
          this.handleTokenise(data.payload as { publicKeyJwk: JsonWebKey });
          break;
        case "update_appearance":
          this.handleUpdateAppearance(data.payload as { appearance: Appearance });
          break;
      }
    };

    window.addEventListener("message", this.messageHandler);
  }

  /** Re-apply appearance and locale settings received from the merchant page. */
  private handleInit(payload: { appearance?: Appearance; locale?: string; translations?: TranslationOverride }): void {
    if (this.container && payload.appearance) {
      this.computedStyles = StyleEngine.compute(payload.appearance);
      StyleEngine.apply(this.container, this.computedStyles);
    }

    // Re-initialise TranslationManager when locale or translations change
    if (payload.locale !== undefined || payload.translations !== undefined) {
      const locale = payload.locale ?? this.translationManager.getActiveLocale();
      this.translationManager.init(locale, payload.translations);
      if (this.container) {
        RTLDirector.apply(this.container, this.translationManager.isRTL());
      }
    }
  }

  private handleFocus(): void {
    const input = this.getPrimaryInput();
    if (input) {
      input.focus();
      this.sendMessage("focus", { elementType: this.config.elementType });
    }
  }

  private handleBlur(): void {
    const input = this.getPrimaryInput();
    if (input) {
      input.blur();
      this.sendMessage("blur", { elementType: this.config.elementType });
    }
  }

  private handleClear(): void {
    this.cardData = { cardNumber: "", expiry: "", cvc: "" };
    for (const input of this.inputs.values()) {
      input.value = "";
    }
    this.emitChangeEvent();
  }

  /**
   * Handle tokenise action — encrypt card data and send result.
   * Raw card data is NEVER sent in outgoing messages — only JWE or error metadata.
   * Requirements: 13.4, 14.5
   */
  private async handleTokenise(payload: { publicKeyJwk: JsonWebKey }): Promise<void> {
    try {
      const jwe = await JWEEncryptor.encrypt(this.cardData, payload.publicKeyJwk);
      this.sendMessage("tokenise_result", { jwe, error: null });
    } catch (err) {
      this.sendMessage("tokenise_result", {
        jwe: null,
        error: {
          type: "encryption_error",
          code: "encryption_failed",
          message: err instanceof Error ? err.message : "Encryption failed",
          elementType: this.config.elementType,
        },
      });
    }
  }

  private handleUpdateAppearance(payload: { appearance: Appearance }): void {
    if (this.container) {
      this.computedStyles = StyleEngine.compute(payload.appearance);
      StyleEngine.apply(this.container, this.computedStyles);
    }
  }

  /**
   * Send a message to the merchant page via PostMessage.
   * Never includes raw card data — only JWE or metadata.
   * Requirements: 13.4
   */
  sendMessage(action: OutgoingAction, payload: unknown): void {
    const message: OutgoingMessage = { action, payload };
    window.parent.postMessage(message, this.config.merchantOrigin);
  }

  /** Create card-number, expiry, and CVC inputs for the combined "card" element type. */
  private createCombinedInputs(container: HTMLElement): void {
    const t = (key: Parameters<TranslationManager["t"]>[0]) => this.translationManager.t(key);
    const fields = [
      { name: "cardNumber", ariaElementType: "cardNumber", label: t("labels.cardNumber"), placeholder: t("placeholders.cardNumber"), type: "tel" },
      { name: "expiry", ariaElementType: "cardExpiry", label: t("labels.expirationDate"), placeholder: t("placeholders.expirationDate"), type: "tel" },
      { name: "cvc", ariaElementType: "cardCvc", label: t("labels.cvc"), placeholder: t("placeholders.cvc"), type: "tel" },
    ];

    for (const field of fields) {
      const input = this.createInput(container, field.name, field.label, field.placeholder, field.type, field.ariaElementType);
      this.inputs.set(field.name, input);
    }
  }

  /** Create a single input for a split element type (cardNumber, cardExpiry, or cardCvc). */
  private createSingleInput(container: HTMLElement, elementType: string): void {
    const t = (key: Parameters<TranslationManager["t"]>[0]) => this.translationManager.t(key);
    const fieldMap: Record<string, { name: string; labelKey: "labels.cardNumber" | "labels.expirationDate" | "labels.cvc"; placeholderKey: "placeholders.cardNumber" | "placeholders.expirationDate" | "placeholders.cvc" }> = {
      cardNumber: { name: "cardNumber", labelKey: "labels.cardNumber", placeholderKey: "placeholders.cardNumber" },
      cardExpiry: { name: "expiry", labelKey: "labels.expirationDate", placeholderKey: "placeholders.expirationDate" },
      cardCvc: { name: "cvc", labelKey: "labels.cvc", placeholderKey: "placeholders.cvc" },
    };
    const field = fieldMap[elementType];
    if (field) {
      const input = this.createInput(container, field.name, t(field.labelKey), t(field.placeholderKey), "tel", elementType);
      this.inputs.set(field.name, input);
    } else {
      const input = this.createInput(container, elementType, "", "", "tel", elementType);
      this.inputs.set(elementType, input);
    }
  }

  /**
   * Create an HTML input element with ARIA attributes, event listeners, and focus styles.
   * @returns The configured HTMLInputElement appended to the container.
   */
  private createInput(
    container: HTMLElement,
    name: string,
    label: string,
    placeholder: string,
    type: string,
    ariaElementType?: string
  ): HTMLInputElement {
    const input = container.ownerDocument.createElement("input");
    input.type = type;
    input.placeholder = placeholder;
    input.className = "sf-input";
    input.autocomplete = getAutocomplete(name) as typeof input.autocomplete;
    input.name = name;

    // Apply ARIA attributes
    AccessibilityManager.applyAriaAttributes(input, {
      elementType: ariaElementType ?? this.config.elementType,
      required: true,
      label: label,
      translate: (key) => this.translationManager.t(key),
    });
    AccessibilityManager.makeTabbable(input);

    // Apply focus styles
    AccessibilityManager.applyFocusStyles(container);

    // Wire input events
    const inputHandler = () => this.handleInputChange(name, input.value);
    input.addEventListener("input", inputHandler);

    // Handle paste events (Req 11.1)
    const pasteHandler = (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;
      const pasted = clipboardEvent.clipboardData?.getData("text") ?? "";
      if (pasted) {
        // Let the default paste happen, then validate on next tick
        setTimeout(() => this.handleInputChange(name, input.value), 0);
      }
    };
    input.addEventListener("paste", pasteHandler);

    // Focus/blur events
    const focusHandler = () => {
      this.sendMessage("focus", { elementType: this.config.elementType });
    };
    const blurHandler = () => {
      this.sendMessage("blur", { elementType: this.config.elementType });
    };
    input.addEventListener("focus", focusHandler);
    input.addEventListener("blur", blurHandler);

    // Escape key handler
    const cleanupKeyboard = AccessibilityManager.setupKeyboardHandlers(
      input,
      this.config.elementType,
      (elementType) => {
        this.sendMessage("escape", { elementType });
      }
    );

    this.cleanupFns.push(
      () => input.removeEventListener("input", inputHandler),
      () => input.removeEventListener("paste", pasteHandler),
      () => input.removeEventListener("focus", focusHandler),
      () => input.removeEventListener("blur", blurHandler),
      cleanupKeyboard
    );

    container.appendChild(input);
    return input;
  }

  /** Update internal card data for the changed field and re-emit a change event. */
  private handleInputChange(fieldName: string, value: string): void {
    // Update internal card data
    if (fieldName === "cardNumber") this.cardData.cardNumber = value.replace(/\s/g, "");
    else if (fieldName === "expiry") this.cardData.expiry = value;
    else if (fieldName === "cvc") this.cardData.cvc = value;

    this.emitChangeEvent();

    // Update aria-invalid based on validation
    const input = this.inputs.get(fieldName);
    if (input) {
      const result = this.validateField(fieldName);
      AccessibilityManager.setInvalid(input, result.error !== null);
    }
  }

  /** Run the appropriate validation for a single field and return the result. */
  private validateField(fieldName: string): FieldValidationResult {
    switch (fieldName) {
      case "cardNumber":
        return ValidationEngine.validateCardNumber(this.cardData.cardNumber);
      case "expiry":
        return ValidationEngine.validateExpiry(this.cardData.expiry);
      case "cvc":
        return ValidationEngine.validateCVC(this.cardData.cvc, this.getCurrentBrand());
      default:
        return { valid: false, complete: false, empty: true, brand: null, error: null };
    }
  }

  private getCurrentBrand(): CardBrand | null {
    return ValidationEngine.validateCardNumber(this.cardData.cardNumber).brand;
  }

  /**
   * Localise an error object by mapping its code to a translated message.
   * Keeps error.code as the stable English identifier for programmatic matching.
   * Requirements: 10.1–10.7
   */
  private localiseError(error: { code: string; message: string } | null): { code: string; message: string } | null {
    if (!error) return null;
    const translationKey = ERROR_CODE_TO_KEY[error.code];
    if (translationKey) {
      return { code: error.code, message: this.translationManager.t(translationKey) };
    }
    return error;
  }

  /** Validate all fields, localise errors, and post a change event to the merchant page. */
  private emitChangeEvent(): void {
    let payload: ChangeEventPayload;

    if (this.config.elementType === "card") {
      const cn = ValidationEngine.validateCardNumber(this.cardData.cardNumber);
      const exp = ValidationEngine.validateExpiry(this.cardData.expiry);
      const cvc = ValidationEngine.validateCVC(this.cardData.cvc, cn.brand);
      payload = buildCombinedChangePayload(this.config.elementType, cn, exp, cvc);
    } else {
      const fieldName = elementTypeToFieldName(this.config.elementType);
      const result = this.validateField(fieldName);
      payload = buildChangePayload(this.config.elementType, result);
    }

    // Localise error message before emitting
    payload.error = this.localiseError(payload.error);

    this.sendMessage("change", payload);
  }

  private getPrimaryInput(): HTMLInputElement | null {
    if (this.config.elementType === "card") {
      return this.inputs.get("cardNumber") ?? null;
    }
    const fieldName = elementTypeToFieldName(this.config.elementType);
    return this.inputs.get(fieldName) ?? null;
  }

  /**
   * Clean up all event listeners and DOM elements.
   */
  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];

    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }

    this.inputs.clear();
  }
}

function elementTypeToFieldName(elementType: string): string {
  switch (elementType) {
    case "cardNumber": return "cardNumber";
    case "cardExpiry": return "expiry";
    case "cardCvc": return "cvc";
    default: return elementType;
  }
}

function getAutocomplete(fieldName: string): string {
  switch (fieldName) {
    case "cardNumber": return "cc-number";
    case "expiry": return "cc-exp";
    case "cvc": return "cc-csc";
    default: return "off";
  }
}
